import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import "../../styles/EnvironmentalContext.css";
import EvidenceReferenceField from "../../components/EvidenceReferenceField";
import {
  approveEnvironmentalContextApi,
  canApproveEmsModule,
  fetchEnvironmentalContextApi,
  updateEnvironmentalContextApi,
} from "../../lib/api";

const EMPTY_FORM = {
  title: "Environmental Context and EMS Scope",
  organisation_profile: "",
  internal_issues: "",
  external_issues: "",
  environmental_conditions: "",
  climate_change_relevance: "",
  strategic_direction: "",
  interested_parties: "",
  needs_expectations: "",
  ems_scope: "",
  life_cycle_considerations: "",
  review_date: "",
  version: "1.0",
  evidence: "",
  notes: "",
};

export default function EnvironmentalContext() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [record, setRecord] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const canApprove = canApproveEmsModule("environmental_context");

  const loadRecord = async () => {
    try {
      setLoading(true);
      const data = await fetchEnvironmentalContextApi();
      setRecord(data || null);
      setForm({ ...EMPTY_FORM, ...(data || {}) });
    } catch (err) {
      toast.error(err?.message || "Failed to load environmental context");
      setRecord(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecord();
  }, []);

  const summary = useMemo(() => ([
    { label: "Status", value: String(record?.status || "draft") },
    { label: "Version", value: String(record?.version || "1.0") },
    { label: "Approved by", value: String(record?.approved_by || "-") || "-" },
    { label: "Review date", value: String(record?.review_date || "-") || "-" },
  ]), [record]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      const updated = await updateEnvironmentalContextApi(form);
      setRecord(updated || null);
      setForm({ ...EMPTY_FORM, ...(updated || {}) });
      toast.success("Environmental context saved");
    } catch (err) {
      toast.error(err?.message || "Failed to save environmental context");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    try {
      setApproving(true);
      const notes = window.prompt("Optional approval note:", "") || "";
      const response = await approveEnvironmentalContextApi({ notes });
      setRecord(response?.context || null);
      setForm({ ...EMPTY_FORM, ...(response?.context || {}) });
      toast.success("Environmental context approved");
    } catch (err) {
      toast.error(err?.message || "Failed to approve environmental context");
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="dashboard-page environmental-context-page">
      <div className="dashboard-header">
        <div>
          <p className="environmental-context-eyebrow">ISO 14001:2026 Clause 4</p>
          <h1>Environmental Context</h1>
          <p className="subtitle">Record the organisational context, environmental conditions, interested parties and EMS scope that inform your environmental management system.</p>
        </div>
      </div>

      <div className="summary-row">
        {summary.map((item) => (
          <div className="kpi-card" key={item.label}>
            <div className="kpi-label">{item.label}</div>
            <h2>{loading ? "-" : item.value}</h2>
          </div>
        ))}
      </div>

      <section className="card environmental-context-guidance">
        <div>
          <p className="environmental-context-eyebrow">Before you start</p>
          <h3>Describe the conditions that shape your EMS</h3>
          <p>Use this record to show how your organisation operates, what environmental conditions may affect it, and what it needs to manage in response. Review it when the business, its locations, its activities or relevant environmental conditions change.</p>
        </div>
        <div className="environmental-context-guidance-grid">
          <div><strong>1. Understand context</strong><span>Identify internal and external issues, including relevant climate, water, biodiversity, resource or local environmental conditions.</span></div>
          <div><strong>2. Consider stakeholders</strong><span>Record interested parties and the needs, expectations or obligations that affect the EMS.</span></div>
          <div><strong>3. Set the boundary</strong><span>Define the legal entity, sites, activities, services and lifecycle stages covered by the EMS.</span></div>
        </div>
      </section>

      <div className="card settings-card environmental-context-form">
        <div className="settings-header">
          <div>
            <p className="environmental-context-eyebrow">Your Clause 4 record</p>
            <h3>Complete the environmental context</h3>
          </div>
          <span className="kpi-sub">Edits reset approval until an authorised approver signs off again.</span>
        </div>
        <form onSubmit={handleSave}>
          <div className="settings-grid">
            <label className="settings-field">
              <span>Record title</span>
              <input placeholder="e.g. Hindsight Sustainability Environmental Context and EMS Scope" value={form.title} onChange={(e) => setField("title", e.target.value)} disabled={saving || loading} />
              <small>Use a clear title that identifies the organisation and this controlled record.</small>
            </label>
            <label className="settings-field">
              <span>Version</span>
              <input placeholder="e.g. 1.0" value={form.version} onChange={(e) => setField("version", e.target.value)} disabled={saving || loading} />
              <small>Increase this when an approved context record changes materially.</small>
            </label>
            <label className="settings-field">
              <span>Review date</span>
              <input type="date" value={form.review_date} onChange={(e) => setField("review_date", e.target.value)} disabled={saving || loading} />
              <small>Set the next planned review, or review sooner after a significant change.</small>
            </label>
            <EvidenceReferenceField
              label="Evidence link or attachment reference"
              value={form.evidence}
              onChange={(value) => setField("evidence", value)}
              disabled={saving || loading}
              helpText="Link a context analysis, stakeholder map, site plan, management-review minutes or another supporting record."
            />
            <label className="settings-field environmental-context-wide-field">
              <span>Organisation profile</span>
              <small>Describe the legal entity, purpose, key products or services, locations, workforce and main activities covered by this assessment.</small>
              <textarea rows={4} value={form.organisation_profile} onChange={(e) => setField("organisation_profile", e.target.value)} disabled={saving || loading} placeholder="e.g. Hindsight Sustainability provides sustainability consulting from its UK office and supports clients through digital reporting services." />
            </label>
            <label className="settings-field environmental-context-wide-field">
              <span>Internal issues</span>
              <small>Record internal factors that could affect the EMS, such as governance, resources, competence, processes, technology, culture or operational changes.</small>
              <textarea rows={4} value={form.internal_issues} onChange={(e) => setField("internal_issues", e.target.value)} disabled={saving || loading} placeholder="e.g. Limited staff capacity during peak reporting periods; reliance on cloud systems; growing need for environmental competence across delivery teams." />
            </label>
            <label className="settings-field environmental-context-wide-field">
              <span>External issues</span>
              <small>Record external factors such as legislation, market expectations, supplier dependency, local community concerns, economic conditions or sector changes.</small>
              <textarea rows={4} value={form.external_issues} onChange={(e) => setField("external_issues", e.target.value)} disabled={saving || loading} placeholder="e.g. Increasing client demand for verified emissions information; evolving UK environmental regulation; supplier data availability." />
            </label>
            <label className="settings-field environmental-context-wide-field">
              <span>Environmental conditions</span>
              <small>Consider conditions that may affect the organisation and conditions the organisation may affect, including climate hazards, water stress, biodiversity, air quality, waste infrastructure or local sensitivities.</small>
              <textarea rows={5} value={form.environmental_conditions} onChange={(e) => setField("environmental_conditions", e.target.value)} disabled={saving || loading} placeholder="e.g. The organisation operates from an urban office with low direct land impact. Relevant conditions include climate-related disruption to travel and supply chains, local waste services and electricity-grid emissions." />
            </label>
            <label className="settings-field environmental-context-wide-field">
              <span>Climate-change relevance</span>
              <small>State whether climate change is relevant to the EMS and why. Record relevant physical or transition effects, or explain why none were identified.</small>
              <textarea rows={4} value={form.climate_change_relevance} onChange={(e) => setField("climate_change_relevance", e.target.value)} disabled={saving || loading} placeholder="e.g. Climate change is relevant because severe weather can disrupt client travel and supplier services, while customer expectations and emissions reporting requirements influence our service delivery." />
            </label>
            <label className="settings-field environmental-context-wide-field">
              <span>Purpose, strategic direction and business decisions</span>
              <small>Explain how the environmental context connects to the organisation&apos;s purpose, strategy and material decisions such as growth, sites, procurement, service design or investment.</small>
              <textarea rows={4} value={form.strategic_direction} onChange={(e) => setField("strategic_direction", e.target.value)} disabled={saving || loading} placeholder="e.g. Planned growth in digital reporting services increases reliance on cloud suppliers, so procurement and service-design decisions consider energy use, resilience and supplier environmental performance." />
            </label>
            <label className="settings-field environmental-context-wide-field">
              <span>Interested parties</span>
              <small>List parties that can affect, be affected by or perceive themselves affected by the EMS, for example customers, employees, regulators, suppliers, landlords, investors and local communities.</small>
              <textarea rows={4} value={form.interested_parties} onChange={(e) => setField("interested_parties", e.target.value)} disabled={saving || loading} placeholder="e.g. Clients, employees, contractors, regulators, the landlord, key suppliers and local waste-service providers." />
            </label>
            <label className="settings-field environmental-context-wide-field">
              <span>Needs, expectations and compliance obligations</span>
              <small>State the relevant needs and expectations, and identify which ones the organisation must meet or chooses to meet as compliance obligations.</small>
              <textarea rows={4} value={form.needs_expectations} onChange={(e) => setField("needs_expectations", e.target.value)} disabled={saving || loading} placeholder="e.g. Clients expect accurate emissions data; employees expect safe waste arrangements; applicable waste and duty-of-care obligations are maintained in the Legal Compliance Register." />
            </label>
            <label className="settings-field environmental-context-wide-field">
              <span>EMS scope</span>
              <small>Define the boundaries of the EMS: legal entity, sites, functions, activities, products or services, and any justified boundary decisions.</small>
              <textarea rows={5} value={form.ems_scope} onChange={(e) => setField("ems_scope", e.target.value)} disabled={saving || loading} placeholder="e.g. The EMS covers Hindsight Sustainability's UK operations, office activities, client service delivery and controlled contractors. It includes energy, business travel, procurement, waste and digital-service operations." />
            </label>
            <label className="settings-field environmental-context-wide-field">
              <span>Lifecycle considerations</span>
              <small>Describe the lifecycle stages the organisation can control or influence, including procurement, delivery, use, end-of-life or supplier engagement.</small>
              <textarea rows={4} value={form.life_cycle_considerations} onChange={(e) => setField("life_cycle_considerations", e.target.value)} disabled={saving || loading} placeholder="e.g. Procurement of office equipment and digital services, travel choices, client-delivery methods, waste handling and supplier engagement are considered in relevant controls and aspects." />
            </label>
            <label className="settings-field environmental-context-wide-field">
              <span>Notes</span>
              <small>Optional: record consultation, sources used, review triggers or links to related risks, aspects, legal obligations and objectives.</small>
              <textarea rows={3} value={form.notes} onChange={(e) => setField("notes", e.target.value)} disabled={saving || loading} placeholder="e.g. Reviewed following office relocation and annual management review. Related actions are tracked in Risk & Opportunities and Actions & Deadlines." />
            </label>
          </div>
          <div className="settings-actions">
            <button className="btn-primary" type="submit" disabled={saving || loading}>{saving ? "Saving..." : "Save environmental context"}</button>
            {canApprove ? (
              <button className="btn-secondary" type="button" disabled={approving || loading} onClick={handleApprove}>{approving ? "Approving..." : "Approve context"}</button>
            ) : null}
          </div>
        </form>
      </div>
    </div>
  );
}
